import React, {
  useCallback,
  useEffect,
  useRef,
  /* useRef, */ useState
} from 'react'
import TreeView from '@mui/lab/TreeView'
import {
  Box,
  Chip,
  InputBase,
  MenuItem,
  OutlinedInput,
  Popover,
  PopoverActions,
  Select,
  SelectProps
} from '@mui/material'
import CustomTreeItem from './components/customTreeItem/CustomTreeItem'
import { useTranslation } from 'react-i18next'
import SearchIcon from '@mui/icons-material/Search'
import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { EntityType } from '../../api/models/apiTypes.model'
import { customTreeUtils } from './utils/CustomTree.utils'
import debounce from 'lodash.debounce'

export interface TreeItemI {
  label: string
  id: string
  checked: boolean
  icon?: React.ReactNode
  treeItems?: TreeItemI[]
  indeterminate?: boolean
  type?: EntityType
}
interface PropsI {
  treeItems: TreeItemI[]
  onChange: (newTreeItems: TreeItemI[]) => void
  error?: boolean
  errorMessage?: string
  inputRef?: React.Ref<any>
  variant?: 'default' | 'white'
  selectProps?: Partial<SelectProps>
}

const CustomTree: ReactFC<PropsI> = ({
  treeItems,
  onChange,
  error,
  inputRef,
  selectProps
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [currentTreeItems, setCurrentTreeItems] = useState(treeItems)
  const anchorEl = useRef<HTMLElement>(null)
  const popOverAction = useRef<PopoverActions>(null)
  const [expandedItems, setExpandedItems] = useState<string[]>([])
  const [searchValue, setSearchValue] = useState('')

  const { t } = useTranslation()

  const checkIfContainsValue = useCallback(
    (treeItems: TreeItemI[], searchValue: string): boolean =>
      treeItems.some(
        (treeItem) =>
          treeItem.label
            .toLocaleLowerCase()
            .includes(searchValue.toLowerCase()) ||
          (treeItem.treeItems != null &&
            checkIfContainsValue(treeItem.treeItems, searchValue))
      ),
    []
  )

  const filterTreeItems = useCallback(
    (treeItems: TreeItemI[], searchValue: string) =>
      treeItems.filter(
        (treeItem) =>
          treeItem.label
            .toLocaleLowerCase()
            .includes(searchValue.toLocaleLowerCase()) ||
          (treeItem.treeItems != null &&
            checkIfContainsValue(treeItem.treeItems, searchValue))
      ),
    [checkIfContainsValue]
  )

  const getFilteredTreeItems = (
    treeItems: TreeItemI[],
    searchValue: string
  ): TreeItemI[] => {
    let newIdsToExpand: string[] = ['1']
    const reduceFilteredTreeItems = (
      treeItems: TreeItemI[],
      searchValue: string
    ): TreeItemI[] => {
      const filteredTreeItems = treeItems.reduce<TreeItemI[]>(
        (acum, treeItem) => {
          if (
            treeItem.label.toLowerCase().includes(searchValue.toLowerCase())
          ) {
            return (acum = [...acum, treeItem])
          }
          if (treeItem.treeItems == null) return acum
          const doFilterContainsValue = checkIfContainsValue(
            treeItem.treeItems,
            searchValue
          )
          if (doFilterContainsValue) {
            newIdsToExpand = [...newIdsToExpand, treeItem.id]
            return (acum = [
              ...acum,
              {
                ...treeItem,
                treeItems: reduceFilteredTreeItems(
                  filterTreeItems(treeItem.treeItems, searchValue),
                  searchValue
                )
              }
            ])
          }
          if (treeItem.treeItems.length === 1) {
            return (acum = [
              ...acum,
              {
                ...treeItem,
                treeItems: reduceFilteredTreeItems(
                  treeItem.treeItems,
                  searchValue
                )
              }
            ])
          }

          return acum
        },
        []
      )
      return filteredTreeItems
    }
    const reducedTreeItems = reduceFilteredTreeItems(treeItems, searchValue)

    setExpandedItems((expanded) => [...expanded, ...newIdsToExpand])
    return reducedTreeItems
  }

  useEffect(() => {
    setCurrentTreeItems(
      searchValue ? getFilteredTreeItems(treeItems, searchValue) : treeItems
    )
  }, [treeItems])

  const handleExpandItem = (
    _event: React.SyntheticEvent,
    nodeIds: string[]
  ) => {
    setExpandedItems(nodeIds)
  }

  const debounceSearch = useCallback(
    debounce((searchValue: string) => {
      if (searchValue) {
        const filteredItems = getFilteredTreeItems(treeItems, searchValue)
        return setCurrentTreeItems(filteredItems)
      }
      setCurrentTreeItems(treeItems)
    }, 300),
    [treeItems]
  )

  const handleSearch = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const searchValue = e.target.value
    debounceSearch(searchValue)
    setSearchValue(searchValue)
  }

  const changeChecked = (
    id: string,
    type: EntityType | undefined,
    value: boolean,
    treeItems: TreeItemI[]
  ) => {
    let updatedTreeItems = customTreeUtils.updateTreeItems(
      id,
      type,
      value,
      treeItems
    )
    updatedTreeItems =
      customTreeUtils.updateTreeItemsByChildrensCheck(updatedTreeItems)
    onChange(updatedTreeItems)
    return updatedTreeItems
  }

  const renderer = (treeItem: TreeItemI) => {
    return (
      <CustomTreeItem
        key={`${treeItem.type}-${treeItem.id}`}
        nodeId={treeItem.id}
        label={treeItem.label}
        checked={treeItem.checked}
        onClick={() => {
          setTimeout(() => {
            popOverAction.current?.updatePosition()
          }, 180)
        }}
        customOnChange={(_id, newCheckedValue) => {
          changeChecked(treeItem.id, treeItem.type, newCheckedValue, treeItems)
        }}
        haveChildren={(treeItem.treeItems?.length ?? 0) > 0}
        indeterminate={treeItem.indeterminate}
        style={{
          position: 'relative',
          left:
            treeItem.treeItems?.length && treeItem.treeItems?.length > 0
              ? '0'
              : '-1.5rem'
        }}
      >
        {treeItem.treeItems &&
          treeItem.treeItems.map((newTreeItem) => renderer(newTreeItem))}
      </CustomTreeItem>
    )
  }

  const getCheckedItems = (treeItems: TreeItemI[]) =>
    treeItems.reduce<TreeItemI[]>((acum, treeItem) => {
      if (treeItem.checked) {
        return (acum = [...acum, treeItem])
      }
      if (treeItem.treeItems?.length) {
        acum = [...acum, ...getCheckedItems(treeItem.treeItems)]
      }
      return acum
    }, [])

  const checkedItems = getCheckedItems(
    customTreeUtils.updateTreeItemsByChildrensCheck(treeItems)
  )
  const chips = getCheckedItems(
    customTreeUtils.updateTreeItemsByChildrensCheck(treeItems)
  ).map((checkedItem) => checkedItem.id)

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%'
      }}
      // onClick={(e) => {
      //   if (e.target === e.currentTarget) {
      //     setIsOpen(false)
      //   }
      // }}
    >
      <Select
        sx={{
          '& .MuiSvgIcon-root': {
            transform: isOpen ? 'rotate(-180deg)' : ''
          },
          minWidth: 300
        }}
        open={false}
        className='tree__container'
        multiple
        ref={anchorEl}
        inputRef={inputRef}
        input={<OutlinedInput />}
        onClick={() => {
          setIsOpen(true)
        }}
        onOpen={() => {
          // setIsOpen(true)
        }}
        error={error}
        value={chips}
        renderValue={(selected: any) => {
          return (
            <Box
              className='tree__chips_container'
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5
              }}
            >
              {selected.map((id: string) => {
                const checkedItem = checkedItems.find((item) => item.id === id)!
                return (
                  <Chip
                    sx={{
                      cursor: 'pointer'
                    }}
                    key={checkedItem.id}
                    label={checkedItem.label}
                    onDelete={() => {
                      changeChecked(
                        checkedItem.id,
                        checkedItem.type,
                        false,
                        treeItems
                      )
                    }}
                  />
                )
              })}
            </Box>
          )
        }}
        {...selectProps}
      >
        {chips.map((chip) => (
          <MenuItem value={chip} key={chip} />
        ))}
      </Select>
      <Popover
        sx={{
          zIndex: 999999999
        }}
        action={popOverAction}
        open={isOpen}
        onClose={() => {
          setIsOpen(false)
        }}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Box
          sx={{
            padding: '1rem'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
              width: '100%',
              paddingLeft: '1rem',
              paddingRight: '1rem',
              paddingTop: '0.5rem',
              paddingBottom: '0.5rem',
              marginBottom: '1rem',
              border: '1px solid #bdbdbd',
              borderRadius: '10px'
            }}
          >
            <InputBase
              sx={{
                width: '100%'
              }}
              onChange={handleSearch}
              placeholder={t('apps.users.filters.search')}
              value={searchValue}
            />
            <SearchIcon
              sx={{
                color: 'gray'
              }}
            />
          </Box>
          <TreeView
            defaultCollapseIcon={<ArrowDropDownIcon />}
            defaultExpandIcon={<ArrowRightIcon />}
            disableSelection
            onNodeToggle={handleExpandItem}
            expanded={expandedItems}
          >
            {currentTreeItems.map((treeItem) => renderer(treeItem))}
          </TreeView>
        </Box>
      </Popover>
    </Box>
  )
}

export default CustomTree
